import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  BarChart3, AlertCircle, RefreshCw,
  Activity, FileText, X, Search, Calendar, History, Loader2, Code2, Download,
  ChevronDown, Check, Tags, Plus, Pencil, Save,
} from 'lucide-react';
import {
  getMonitorState, getMonitorEvents, getMonitorHistory, getMonitorLogfiles,
  monitorSearch, lookupChars,
  listTrcCategories, createTrcCategory, listTrcCodes, upsertTrcCode,
  type MonitorState, type MonitorEvent, type TrcCategory, type TrcCode,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';

const POLL_MS = 5000;

const CAT_COLORS: Record<string, string> = {
  login:'#00e676', disc:'#ff1744', trade:'#ffd600', auction:'#d500f9', item:'#78909c',
  alz:'#ffc107', quest:'#8bc34a', dungeon:'#76ff03', combat:'#ff5722', pvp:'#e91e63',
  guild:'#9c27b0', mail:'#3f51b5', craft:'#ff9800', warp:'#00bcd4', party:'#795548',
  level:'#673ab7', pet:'#ff4081', cash:'#cddc39', hack:'#f44336', war:'#b71c1c',
  gm:'#1976d2', death:'#424242', other:'#607d8b',
};

const CAT_LABELS_PT: Record<string, string> = {
  login:'Login', disc:'Desconexão', trade:'Trade', auction:'Leilão', item:'Item',
  alz:'Alz', quest:'Quest', dungeon:'Dungeon', combat:'Combate', pvp:'PvP',
  guild:'Guild', mail:'Mail', craft:'Craft', warp:'Warp', party:'Party',
  level:'Level Up', pet:'Pet', cash:'Cash', hack:'Hack', war:'War',
  gm:'GM', death:'Morte', other:'Outros',
};

// Extrai "Char N" da descricao pra fazer lookup. Suporta variantes:
//   "Char 42 use Item:5101", "42→123 Item:...", "Seller:42 Buyer:123 ..."
function extractCharIdxs(d: string): number[] {
  const out = new Set<number>();
  // "Char N"
  const reChar = /Char[: ](\d+)/g;
  // "Seller:N" "Buyer:N" "Guild:N" -> seguidos de \d+
  const reKV = /(?:Seller|Buyer|Char|Src|Dst|Guild|From|To):(\d+)/g;
  // "N→M"
  const reArrow = /(\d+)→(\d+)/g;
  let m: RegExpExecArray | null;
  while ((m = reChar.exec(d)))  { const n = parseInt(m[1], 10); if (n > 0) out.add(n); }
  while ((m = reKV.exec(d)))    { const n = parseInt(m[1], 10); if (n > 0) out.add(n); }
  while ((m = reArrow.exec(d))) { out.add(parseInt(m[1], 10)); out.add(parseInt(m[2], 10)); }
  return [...out];
}

// Substitui charIdx por "Nome (Idx)" usando o cache. Mantem texto original
// se nao tiver lookup.
function enrichDescription(d: string, names: Map<number, string>): string {
  if (!names.size) return d;
  // Sub regex: numeros em contextos que ja identificamos
  return d
    .replace(/Char[: ](\d+)/g, (_m, n) => {
      const idx = parseInt(n, 10);
      const nm = names.get(idx);
      return nm ? `Char: ${nm} (${idx})` : `Char: ${idx}`;
    })
    .replace(/(\d+)→(\d+)/g, (_m, a, b) => {
      const ai = parseInt(a, 10), bi = parseInt(b, 10);
      const an = names.get(ai), bn = names.get(bi);
      return `${an ? `${an}(${ai})` : ai}→${bn ? `${bn}(${bi})` : bi}`;
    });
}

type Tab = 'live' | 'history' | 'trcs';

export default function MonitorPage() {
  const [tab, setTab] = useState<Tab>('live');

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
            <BarChart3 className="text-[#00e1d7]" size={26} />
            GameLog
          </h1>
          <p className="text-sm text-white/65 mt-1">
            Eventos do servidor de jogo. Feed em tempo real ou busca histórica nos arquivos.
          </p>
        </div>
      </div>

      {/* Tabs — estilo pill padrao do painel */}
      <div className="flex flex-wrap gap-1 mb-5 surface-card rounded-xl p-1 w-fit max-w-full">
        <TabBtn active={tab === 'live'}    onClick={() => setTab('live')}
          icon={<Activity size={14} />}   label="Tempo Real" />
        <TabBtn active={tab === 'history'} onClick={() => setTab('history')}
          icon={<History size={14} />}    label="Histórico (.GameLog)" />
        <TabBtn active={tab === 'trcs'}    onClick={() => setTab('trcs')}
          icon={<Tags size={14} />}       label="TRCS" />
      </div>

      {tab === 'live' && <LiveFeed />}
      {tab === 'history' && <HistoricalSearch />}
      {tab === 'trcs' && <TrcsTab />}
    </div>
  );
}

// ============================================================== LIVE FEED

function LiveFeed() {
  const [state, setState] = useState<MonitorState | null>(null);
  const [events, setEvents] = useState<MonitorEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCats, setActiveCats] = useState<Set<string>>(new Set());
  const [textFilter, setTextFilter] = useState('');
  const [paused, setPaused] = useState(false);
  const [charNames, setCharNames] = useState<Map<number, string>>(new Map());
  const lookedUpRef = useRef<Set<number>>(new Set());

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const cat = activeCats.size > 0 ? Array.from(activeCats).join(',') : undefined;
      const q = textFilter.trim() || undefined;
      const [s, e] = await Promise.all([
        getMonitorState(),
        getMonitorEvents({ limit: 1000, cat, q }).catch(() => [] as MonitorEvent[]),
      ]);
      setState(s);
      setEvents(e);
      setError(null);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [activeCats, textFilter]);

  useEffect(() => {
    fetchAll();
    if (paused) return;
    const id = setInterval(() => fetchAll(true), POLL_MS);
    return () => clearInterval(id);
  }, [fetchAll, paused]);

  // Resolve char names de chars que aparecem nos events
  useEffect(() => {
    const idxs = new Set<number>();
    for (const ev of events) {
      for (const idx of extractCharIdxs(ev.d || '')) {
        if (!lookedUpRef.current.has(idx)) idxs.add(idx);
      }
    }
    if (idxs.size === 0) return;
    const list = [...idxs];
    list.forEach(i => lookedUpRef.current.add(i));
    lookupChars(list).then(map => {
      setCharNames(prev => {
        const next = new Map(prev);
        for (const [k, v] of Object.entries(map)) {
          if (v) next.set(parseInt(k, 10), v);
        }
        return next;
      });
    }).catch(() => {});
  }, [events]);

  const offline = state && !state.online;
  const counters = state?.counters || {};
  const sortedCats = Object.entries(counters).sort((a, b) => b[1] - a[1]);

  const toggleCat = (cat: string) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const filteredEvents = events.filter((ev) => {
    if (activeCats.size > 0 && !activeCats.has(ev.cat)) return false;
    if (textFilter) {
      const q = textFilter.toLowerCase();
      if (!ev.d?.toLowerCase().includes(q)
          && !ev.label?.toLowerCase().includes(q)
          && !ev.ch?.toLowerCase().includes(q)
          && !ev.trc?.includes(textFilter)) {
        return false;
      }
    }
    return true;
  });

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-5">
        <p className="text-xs text-white/45">
          {state?.day ? `Dia ${state.day}` : ''}
          {state?.totalLines != null && ` · ${state.totalLines.toLocaleString('pt-BR')} eventos processados`}
          {state?.files != null && ` · ${state.files} arquivos`}
        </p>
        <button
          onClick={() => fetchAll()}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-amber-500/10 border border-amber-500/30 text-amber-400
                     hover:bg-amber-500/20 transition cursor-pointer disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Atualizar
        </button>
      </div>

      {offline && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-yellow-500/30">
          <AlertCircle size={18} className="text-yellow-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-yellow-400">Monitor offline</p>
            <p className="text-xs text-white/65 mt-0.5">
              O daemon <code>nexbyts_monitor</code> não está acessível em <code>http://127.0.0.1:8090</code>.
            </p>
          </div>
        </div>
      )}

      {error && !offline && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Categorias */}
        <div className="surface-card rounded-xl p-5 lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider flex items-center gap-2">
              <Activity size={14} className="text-[#00e1d7]" /> Categorias
            </h3>
            {activeCats.size > 0 && (
              <button onClick={() => setActiveCats(new Set())}
                className="text-[10px] text-white/55 hover:text-white flex items-center gap-1 cursor-pointer">
                <X size={10} /> limpar
              </button>
            )}
          </div>
          <p className="text-[10px] text-white/45 mb-2">Clique pra filtrar o feed</p>
          {sortedCats.length === 0 ? (
            <p className="text-xs text-white/40 py-6 text-center">Sem dados</p>
          ) : (
            <div className="space-y-1 max-h-[500px] overflow-y-auto">
              {sortedCats.map(([cat, count]) => {
                const max = sortedCats[0][1] || 1;
                const pct = (count / max) * 100;
                const active = activeCats.has(cat);
                return (
                  <button key={cat} onClick={() => toggleCat(cat)}
                    className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md transition cursor-pointer
                                ${active ? 'bg-white/[0.06]' : 'hover:bg-white/[0.03]'}`}>
                    <span className={`text-[11px] font-semibold w-20 truncate text-left ${active ? 'text-white' : 'text-white/85'}`}>
                      {CAT_LABELS_PT[cat] || cat}
                    </span>
                    <div className="flex-1 h-2.5 rounded bg-white/[0.04] overflow-hidden">
                      <div className="h-full transition-all"
                           style={{ width: `${pct}%`, background: CAT_COLORS[cat] || '#607d8b' }} />
                    </div>
                    <span className="text-[10px] font-mono text-white/55 w-12 text-right">
                      {count.toLocaleString('pt-BR')}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Feed */}
        <div className="surface-card rounded-xl p-5 lg:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider flex items-center gap-2">
              <FileText size={14} className="text-[#00e1d7]" /> Feed
            </h3>
            <span className="text-[10px] text-white/45 ml-auto">
              {filteredEvents.length}/{events.length} eventos · {paused ? 'pausado' : `poll ${POLL_MS/1000}s`}
            </span>
            <button onClick={() => setPaused((p) => !p)}
              className="px-2 py-0.5 rounded text-[10px] font-bold border transition cursor-pointer"
              style={paused ? { background: '#10b98126', borderColor: '#10b98155', color: '#10b981' }
                            : { background: '#ffffff0a', borderColor: '#ffffff15', color: '#ffffff99' }}>
              {paused ? '▶ Continuar' : '⏸ Pausar'}
            </button>
          </div>

          <div className="relative mb-3">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={textFilter} onChange={(e) => setTextFilter(e.target.value)}
              placeholder="Filtrar por TRC, char, label, canal..."
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-8 pr-3 py-1.5
                         text-[11px] text-white placeholder:text-white/40 font-mono
                         focus:outline-none focus:border-[#00e1d7]/40" />
          </div>

          {filteredEvents.length === 0 ? (
            <p className="text-xs text-white/40 py-6 text-center">
              {events.length === 0 ? 'Sem eventos recentes' : 'Nenhum evento bate o filtro'}
            </p>
          ) : (
            <div className="space-y-0.5 max-h-[500px] overflow-y-auto overflow-x-hidden font-mono text-[11px]">
              {filteredEvents.slice(-200).reverse().map((ev, i) => (
                <EventRow key={i} ev={ev} names={charNames} onCatClick={toggleCat} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================== HISTORICAL SEARCH

// Rotulo amigavel do prefixo de serviço/canal (.GameLog).
function svcLabel(prefix: string): string {
  const m = prefix.match(/WorldSvr_\d+_(\d+)/);
  if (m) return `Canal ${parseInt(m[1], 10)}`;
  if (prefix.startsWith('LoginSvr')) return 'Login';
  if (prefix.startsWith('ChatNode')) return 'Chat';
  return prefix;
}

const ALL_CATS = Object.keys(CAT_LABELS_PT);

function HistoricalSearch() {
  const [days, setDays] = useState<string[]>([]);
  const [day, setDay] = useState<string>('');
  const [cats, setCats] = useState<Set<string>>(new Set());     // vazio = todas
  const [svcs, setSvcs] = useState<Set<string>>(new Set());     // vazio = todos
  const [svcOptions, setSvcOptions] = useState<string[]>([]);
  const [userFilter, setUserFilter] = useState('');
  const [charFilter, setCharFilter] = useState('');
  const [trcFilter, setTrcFilter] = useState('');
  const [results, setResults] = useState<MonitorEvent[]>([]);
  const [memFilter, setMemFilter] = useState('');   // busca livre em memoria (client-side)
  const [dlRaw, setDlRaw] = useState(false);        // download inclui a linha raw?
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [charNames, setCharNames] = useState<Map<number, string>>(new Map());

  // Carrega lista de dias disponiveis
  useEffect(() => {
    getMonitorHistory().then(list => {
      const arr = (list || []) as string[];
      setDays(arr);
      if (arr.length > 0) setDay(prev => prev || arr[0]);
    }).catch(() => {});
  }, []);

  // Carrega os serviços/canais disponiveis pro dia selecionado
  useEffect(() => {
    if (!day) { setSvcOptions([]); return; }
    getMonitorLogfiles(day).then(list => {
      setSvcOptions((list || []) as string[]);
      setSvcs(new Set());   // reseta seleção ao trocar de dia
    }).catch(() => setSvcOptions([]));
  }, [day]);

  const toggleCat = (c: string) =>
    setCats(prev => { const n = new Set(prev); n.has(c) ? n.delete(c) : n.add(c); return n; });
  const toggleSvc = (s: string) =>
    setSvcs(prev => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });

  const doSearch = async () => {
    setLoading(true);
    setError(null);
    setResults([]);
    setCharNames(new Map());
    try {
      const r = await monitorSearch({
        day: day || undefined,
        cat: cats.size > 0 ? [...cats].join(',') : undefined,
        svc: svcs.size > 0 ? [...svcs].join(',') : undefined,
        trc: trcFilter.trim() || undefined,
        char: charFilter ? parseInt(charFilter, 10) : undefined,
        user: userFilter ? parseInt(userFilter, 10) : undefined,
        limit: 1000000,   // traz TODAS as linhas do filtro
      });
      setResults(r);

      // resolve names em batch
      const idxs = new Set<number>();
      r.forEach(ev => extractCharIdxs(ev.d || '').forEach(n => idxs.add(n)));
      if (idxs.size > 0) {
        const map = await lookupChars([...idxs]);
        const nm = new Map<number, string>();
        for (const [k, v] of Object.entries(map)) if (v) nm.set(parseInt(k, 10), v);
        setCharNames(nm);
      }
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro na busca');
    } finally {
      setLoading(false);
    }
  };

  // Busca livre em memoria sobre o resultado ja carregado (rapida, client-side)
  const shown = useMemo(() => {
    const q = memFilter.trim().toLowerCase();
    if (!q) return results;
    return results.filter(ev =>
      (ev.d || '').toLowerCase().includes(q) ||
      (ev.label || '').toLowerCase().includes(q) ||
      (ev.ch || '').toLowerCase().includes(q) ||
      (ev.trc || '').includes(memFilter.trim()) ||
      (ev.raw || '').toLowerCase().includes(q),
    );
  }, [results, memFilter]);

  // Baixa os resultados (o que esta' na tela — respeita o filtro em memoria) como
  // documento de auditoria .txt. Sem "incluir raw": so' TRC + descricao (mesmo
  // conteudo visivel na pagina). Com "incluir raw": anexa a linha crua embaixo.
  const doDownload = () => {
    if (shown.length === 0) return;
    const pad = (s: string, n: number) => (s || '').padEnd(n);
    const filters = [
      `canal=${svcs.size ? [...svcs].map(svcLabel).join(',') : 'todos'}`,
      `categorias=${cats.size ? [...cats].join(',') : 'todas'}`,
      `userNum=${userFilter || '-'}`,
      `charIdx=${charFilter || '-'}`,
      `trc=${trcFilter || '-'}`,
    ].join(' | ');
    const now = new Date();
    const head = [
      'Liberty GameLog — Busca histórica (auditoria)',
      `Dia: ${day}`,
      `Filtros: ${filters}`,
      memFilter.trim() ? `Filtro em memória: "${memFilter.trim()}"` : null,
      `Total: ${shown.length} eventos${shown.length !== results.length ? ` (de ${results.length} carregados)` : ''} — raw incluído: ${dlRaw ? 'sim' : 'não'}`,
      `Gerado: ${now.toLocaleString('pt-BR')}`,
      '='.repeat(80),
      dlRaw
        ? '[hora]     CAT        TRC    CANAL    descrição   /   raw'
        : '[hora]     CAT        TRC    CANAL    descrição',
      '-'.repeat(80),
      '',
    ].filter(Boolean).join('\n');

    const body = shown.map(ev => {
      const t = new Date(ev.time).toLocaleTimeString('pt-BR', { hour12: false });
      const desc = enrichDescription(ev.d || '', charNames);
      let row = `[${t}] ${pad(ev.cat, 10)} ${pad(ev.trc, 6)} ${pad(ev.ch, 8)} ${desc}`;
      if (dlRaw && ev.raw) row += `\n${' '.repeat(11)}raw: ${ev.raw}`;
      return row;
    }).join('\n');

    const blob = new Blob([head + body + '\n'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = `${day || 'log'}_${now.toISOString().slice(11, 16).replace(':', '')}`;
    a.download = `gamelog_${stamp}${dlRaw ? '_raw' : ''}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="surface-card rounded-xl p-5 mb-4">
        <div className="flex items-center gap-2 mb-4">
          <History size={14} className="text-[#00e1d7]" />
          <h3 className="text-sm font-bold text-white/85 uppercase tracking-wider">
            Busca em arquivos .GameLog
          </h3>
        </div>
        <p className="text-[11px] text-white/55 mb-4 leading-relaxed">
          Lê os arquivos do dia direto no servidor e traz <b>todas</b> as linhas do filtro.
          Selecionar o canal deixa a busca bem mais rápida. Sem filtro de char/canal
          em um dia cheio pode demorar.
        </p>

        {/* Linha 1: dia, usernum, charid, trc */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <div>
            <label className="text-[10px] text-white/55 mb-1 block flex items-center gap-1">
              <Calendar size={10} /> Dia
            </label>
            <select value={day} onChange={(e) => setDay(e.target.value)}
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white">
              {days.length === 0 ? (
                <option value="">(sem histórico)</option>
              ) : days.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] text-white/55 mb-1 block">UserNum (conta)</label>
            <input value={userFilter}
              onChange={(e) => setUserFilter(e.target.value.replace(/\D/g, ''))}
              placeholder="ex: 1424"
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white font-mono" />
          </div>
          <div>
            <label className="text-[10px] text-white/55 mb-1 block">CharIdx</label>
            <input value={charFilter}
              onChange={(e) => setCharFilter(e.target.value.replace(/\D/g, ''))}
              placeholder="ex: 8928"
              className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white font-mono" />
          </div>
          <div>
            <label className="text-[10px] text-white/55 mb-1 block">TRC (código ou nome, opcional)</label>
            <TrcCombo value={trcFilter} onChange={setTrcFilter} />
          </div>
        </div>

        {/* Categorias + Canal/Serviço (multi-select dropdown) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
          <MultiSelect
            label="Categorias"
            allLabel="Todas as categorias"
            selected={cats}
            onToggle={toggleCat}
            onClear={() => setCats(new Set())}
            options={ALL_CATS.map(c => ({ value: c, label: CAT_LABELS_PT[c] || c }))}
            colorFor={(c) => CAT_COLORS[c]}
          />
          <MultiSelect
            label={`Canal / Serviço${svcOptions.length > 0 ? ` (${svcOptions.length} no dia)` : ''}`}
            allLabel="Todos os canais"
            selected={svcs}
            onToggle={toggleSvc}
            onClear={() => setSvcs(new Set())}
            options={svcOptions.map(s => ({ value: s, label: svcLabel(s) }))}
          />
        </div>

        <button onClick={doSearch} disabled={loading || !day}
          className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-400
                     text-[#0d141b] font-bold text-sm transition cursor-pointer
                     disabled:opacity-50 flex items-center gap-2">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Buscar
        </button>
      </div>

      {error && (
        <div className="surface-card rounded-xl p-4 mb-4 flex items-start gap-3 border-red-500/30">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      <div className="surface-card rounded-xl p-4">
        <div className="flex items-center mb-3 gap-2 flex-wrap">
          {/* esquerda: botão download + checkbox incluir raw */}
          {results.length > 0 && (
            <>
              <button onClick={doDownload}
                className="px-3 py-1.5 rounded-lg bg-[#00e1d7]/15 hover:bg-[#00e1d7]/25
                           border border-[#00e1d7]/30 text-[#00e1d7] font-semibold text-[11px]
                           transition cursor-pointer flex items-center gap-1.5 shrink-0"
                title={`Baixar ${shown.length} eventos como .txt${dlRaw ? ' (com raw)' : ''}`}>
                <Download size={13} /> Download
              </button>
              <label className="flex items-center gap-1.5 cursor-pointer select-none
                                text-[11px] text-white/60 hover:text-white/85 shrink-0">
                <input type="checkbox" checked={dlRaw} onChange={(e) => setDlRaw(e.target.checked)}
                  className="accent-[#00e1d7] cursor-pointer" />
                incluir raw
              </label>
            </>
          )}

          {/* meio: filtro em memória */}
          {results.length > 0 && (
            <div className="relative flex-1 min-w-[150px] max-w-[300px]">
              <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input value={memFilter} onChange={(e) => setMemFilter(e.target.value)}
                placeholder="filtrar resultado (em memória)..."
                className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg pl-8 pr-3 py-1.5
                           text-[11px] text-white placeholder:text-white/40 font-mono
                           focus:outline-none focus:border-[#00e1d7]/40" />
            </div>
          )}

          {/* direita: contador */}
          <h3 className="text-sm font-bold text-white/85 whitespace-nowrap ml-auto">
            {results.length === 0 ? 'Sem resultados' : `${shown.length}/${results.length} eventos`}
          </h3>
        </div>
        {loading ? (
          <div className="py-12 flex flex-col items-center gap-2">
            <Loader2 className="animate-spin text-white/40" />
            <span className="text-[11px] text-white/45">Lendo os .GameLog do dia… (pode demorar)</span>
          </div>
        ) : results.length === 0 ? (
          <p className="text-xs text-white/40 py-6 text-center">
            {day ? 'Use os filtros e clique em Buscar' : 'Selecione um dia primeiro'}
          </p>
        ) : (
          <div className="space-y-0.5 max-h-[600px] overflow-y-auto overflow-x-hidden font-mono text-[11px]">
            {shown.map((ev, i) => (
              <EventRow key={i} ev={ev} names={charNames} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Combobox de TRC: editável (digita código ou nome) + dropdown com os TRCs
// cadastrados (código — nome — categoria). Seleciona vira chip; também aceita
// código digitado direto (Enter/vírgula). Emite CSV compatível com o filtro.
function TrcCombo({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const codes = useMemo(() => value.split(',').map(s => s.trim()).filter(Boolean), [value]);
  const [query, setQuery] = useState('');
  const [opts, setOpts] = useState<TrcCode[]>([]);
  const [open, setOpen] = useState(false);
  const [labels, setLabels] = useState<Record<string, string>>({});
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    if (!query.trim()) { setOpts([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await listTrcCodes({ search: query.trim(), limit: 25 });
        setOpts(res.items);
        setLabels(prev => {
          const m = { ...prev };
          for (const c of res.items) if (c.eventName) m[String(c.trc)] = c.eventName;
          return m;
        });
      } catch { setOpts([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const add = (code: string) => {
    const c = String(code).trim().replace(/\D/g, '');
    if (!c) return;
    if (!codes.includes(c)) onChange([...codes, c].join(','));
    setQuery(''); setOpts([]);
  };
  const remove = (code: string) => onChange(codes.filter(c => c !== code).join(','));

  return (
    <div className="relative" ref={boxRef}>
      <div className="w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-2 py-1.5 flex flex-wrap items-center gap-1 min-h-[38px] cursor-text"
           onClick={() => setOpen(true)}>
        {codes.map(c => (
          <span key={c} className="inline-flex items-center gap-1 bg-[#00e1d7]/12 border border-[#00e1d7]/25 text-[#00e1d7] rounded px-1.5 py-0.5 text-[10px] font-mono">
            {c}{labels[c] ? <span className="text-[#00e1d7]/60">· {labels[c]}</span> : null}
            <button onClick={(e) => { e.stopPropagation(); remove(c); }} className="hover:text-white cursor-pointer"><X size={10} /></button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') {
              const q = query.trim();
              if (/^\d+$/.test(q)) { e.preventDefault(); add(q); }
              else if (opts.length) { e.preventDefault(); add(String(opts[0].trc)); }
            } else if (e.key === 'Backspace' && !query && codes.length) {
              remove(codes[codes.length - 1]);
            }
          }}
          placeholder={codes.length ? '' : 'TRC ou nome do evento...'}
          className="flex-1 min-w-[90px] bg-transparent text-sm text-white placeholder:text-white/35 outline-none font-mono py-0.5" />
      </div>
      {open && opts.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-[240px] overflow-y-auto bg-[#0d141b] border border-white/[0.08] rounded-lg shadow-xl">
          {opts.map(o => (
            <button key={o.trc} onClick={() => add(String(o.trc))}
              className="w-full text-left px-3 py-1.5 hover:bg-white/[0.05] flex items-center gap-2 cursor-pointer">
              <span className="font-mono text-[11px] text-white/80 w-14 shrink-0">{o.trc}</span>
              <span className="text-xs text-white/70 truncate flex-1">
                {o.eventName || <span className="italic text-white/30">sem nome</span>}
              </span>
              {o.categoryName && <span className="text-[9px] text-white/40 shrink-0">{o.categoryName}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Multi-select em dropdown com checkboxes — botão compacto igual ao <select> de
// "Dia" (mesmo bg/border), abre um painel com "Todas" no topo + cada opção com
// checkbox. Fecha ao clicar fora ou Esc. Substitui a parede de chips.
function MultiSelect({ label, options, selected, onToggle, onClear, allLabel, colorFor }: {
  label: string;
  options: { value: string; label: string }[];
  selected: Set<string>;
  onToggle: (v: string) => void;
  onClear: () => void;
  allLabel: string;
  colorFor?: (v: string) => string | undefined;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const count = selected.size;
  const summary = count === 0
    ? allLabel
    : count <= 2
      ? options.filter(o => selected.has(o.value)).map(o => o.label).join(', ')
      : `${count} selecionadas`;

  const box = (on: boolean) => (
    <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition
                     ${on ? 'bg-[#00e1d7] border-[#00e1d7]' : 'border-white/25'}`}>
      {on && <Check size={11} className="text-[#0d141b]" strokeWidth={3} />}
    </span>
  );

  return (
    <div className="relative" ref={ref}>
      <label className="text-[10px] text-white/55 mb-1 block">{label}</label>
      <button type="button" onClick={() => setOpen(v => !v)}
        className={`w-full flex items-center justify-between gap-2 bg-[#0d141b] border rounded-lg
                   px-3 py-2 text-sm text-left transition
                   ${open ? 'border-[#00e1d7]/40' : 'border-white/[0.06] hover:border-white/15'}`}>
        <span className={`truncate ${count === 0 ? 'text-white/45' : 'text-white'}`}>{summary}</span>
        <div className="flex items-center gap-1 shrink-0">
          {count > 0 && (
            <span role="button" onClick={(e) => { e.stopPropagation(); onClear(); }}
              className="text-white/40 hover:text-white/85 transition" title="Limpar seleção">
              <X size={13} />
            </span>
          )}
          <ChevronDown size={14} className={`text-white/40 transition ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {open && (
        <div className="absolute z-40 mt-1 w-full min-w-[220px] max-h-[300px] overflow-y-auto
                        bg-[#0d141b] border border-white/10 rounded-lg shadow-2xl p-1">
          <button type="button" onClick={onClear}
            className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px]
                       hover:bg-white/[0.05] text-left transition">
            {box(count === 0)}
            <span className={count === 0 ? 'text-white font-medium' : 'text-white/70'}>{allLabel}</span>
          </button>
          <div className="h-px bg-white/[0.06] my-1" />
          {options.map(o => {
            const on = selected.has(o.value);
            const c = colorFor?.(o.value);
            return (
              <button key={o.value} type="button" onClick={() => onToggle(o.value)}
                className="w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded-md text-[12px]
                           hover:bg-white/[0.05] text-left transition">
                {box(on)}
                {c && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c }} />}
                <span className={on ? 'text-white' : 'text-white/70'}>{o.label}</span>
              </button>
            );
          })}
          {options.length === 0 && (
            <div className="px-2.5 py-2 text-[11px] text-white/35">nenhuma opção</div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================== Compartilhado

// Converte a linha crua do GameLog no hex total dos bytes UTF-8 (agrupado em
// pares por byte). Sem tentar adivinhar campo — só o dump fiel.
function toHexTotal(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let out = '';
  for (let i = 0; i < bytes.length; i++) out += bytes[i].toString(16).padStart(2, '0') + ' ';
  return out.trim();
}

function EventRow({ ev, names, onCatClick }: {
  ev: MonitorEvent; names: Map<number, string>; onCatClick?: (cat: string) => void;
}) {
  const enriched = useMemo(() => enrichDescription(ev.d || '', names), [ev.d, names]);
  const [showRaw, setShowRaw] = useState(false);
  const raw = ev.raw || '';
  const hasRaw = !!raw;
  const hex = useMemo(() => (showRaw && hasRaw ? toHexTotal(raw) : ''), [showRaw, hasRaw, raw]);
  return (
    <div className="border-b border-white/[0.03] last:border-0 overflow-hidden">
      <div className="flex items-start gap-2 py-1 min-w-0">
        <span className="text-white/35 shrink-0 w-16">
          {new Date(ev.time).toLocaleTimeString('pt-BR', { hour12: false })}
        </span>
        <span
          className="px-1.5 py-0 rounded text-[9px] font-bold uppercase shrink-0 cursor-pointer"
          style={{ background: `${ev.catColor || CAT_COLORS[ev.cat] || '#607d8b'}20`, color: ev.catColor || CAT_COLORS[ev.cat] || '#607d8b' }}
          onClick={() => onCatClick?.(ev.cat)}
          title="Categoria"
        >
          {ev.catLabel || CAT_LABELS_PT[ev.cat] || ev.cat}
        </span>
        <span className="text-[9px] font-mono text-white/40 shrink-0 w-11"
              title={ev.label}>{ev.trc}</span>
        <span className="text-[10px] text-white/55 shrink-0 w-[130px] truncate"
              title={ev.label}>{ev.label}</span>
        <span className="text-white/45 shrink-0 w-12 truncate">{ev.ch}</span>
        <span className="text-white/85 flex-1 min-w-0 truncate" title={`${ev.label}: ${enriched}`}>
          {enriched}
        </span>
        {hasRaw && (
          <button
            onClick={() => setShowRaw(v => !v)}
            className={`px-1.5 py-0.5 rounded text-[10px] shrink-0 cursor-pointer transition
                       ${showRaw ? 'text-[#00e1d7] bg-[#00e1d7]/10' : 'text-white/55 hover:text-[#00e1d7] hover:bg-[#00e1d7]/10'}`}
            title="Ver raw (hex total da linha)"
          >
            <Code2 size={12} />
          </button>
        )}
      </div>
      {showRaw && hasRaw && (
        <div className="ml-[88px] mb-2 mr-2 bg-[#0a0f15] border border-white/[0.05] rounded p-2 text-[10px]">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-white/55 font-semibold">{ev.label} — raw</span>
            <span className="text-white/35">{new TextEncoder().encode(raw).length} bytes</span>
          </div>
          <div className="mb-1">
            <div className="text-white/40 font-bold mb-0.5">linha</div>
            <div className="text-white/80 font-mono break-all whitespace-pre-wrap select-all">{raw}</div>
          </div>
          <div>
            <div className="text-white/40 font-bold mb-0.5">hex total</div>
            <div className="text-amber-300/80 font-mono break-all select-all leading-relaxed">{hex}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void; icon: React.ReactNode; label: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active
          ? 'bg-[#00e1d7]/15 text-[#00e1d7]'
          : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
    >
      {icon} {label}
    </button>
  );
}

// ============================================================== TRCS (cadastro)
// Cadastro de categorias + códigos TRC. O que for registrado aqui a API sobrepõe
// no feed (nome do evento + tag de categoria). Assim o time vai ficando esperto
// no TRC (ex: 12764 = abrir special box) sem depender de redeploy do monitor.

function TrcsTab() {
  const { notify } = useToast();
  const [cats, setCats] = useState<TrcCategory[]>([]);
  const [codes, setCodes] = useState<TrcCode[]>([]);
  const [total, setTotal] = useState(0);
  const [activeCat, setActiveCat] = useState<number | null>(null);
  const [onlyUnnamed, setOnlyUnnamed] = useState(false);
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [newCatOpen, setNewCatOpen] = useState(false);
  const [editCode, setEditCode] = useState<TrcCode | null>(null);
  const LIMIT = 100;

  const loadCats = useCallback(async () => {
    try { setCats(await listTrcCategories()); }
    catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
  }, [notify]);

  const loadCodes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listTrcCodes({
        categoryId: activeCat ?? undefined, search: applied || undefined,
        onlyUnnamed, skip: page * LIMIT, limit: LIMIT,
      });
      setCodes(res.items); setTotal(res.total);
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setLoading(false); }
  }, [activeCat, applied, onlyUnnamed, page, notify]);

  useEffect(() => { loadCats(); }, [loadCats]);
  useEffect(() => { loadCodes(); }, [loadCodes]);

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const catById = useMemo(() => {
    const m: Record<number, TrcCategory> = {};
    for (const c of cats) m[c.id] = c;
    return m;
  }, [cats]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
      {/* Categorias */}
      <div className="surface-card rounded-xl p-3 h-fit">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-black uppercase tracking-wider text-white/55">Categorias</span>
          <button onClick={() => setNewCatOpen(true)}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold bg-[#00e1d7]/10 border border-[#00e1d7]/25 text-[#00e1d7] hover:bg-[#00e1d7]/20 cursor-pointer">
            <Plus size={12} /> Nova
          </button>
        </div>
        <button onClick={() => { setActiveCat(null); setPage(0); }}
          className={`w-full text-left px-2 py-1.5 rounded-lg text-xs mb-0.5 cursor-pointer transition ${activeCat === null ? 'bg-white/[0.06] text-white' : 'text-white/60 hover:bg-white/[0.03]'}`}>
          Todas <span className="text-white/35">({total.toLocaleString('pt-BR')})</span>
        </button>
        <div className="max-h-[60vh] overflow-y-auto">
          {cats.map((c) => (
            <button key={c.id} onClick={() => { setActiveCat(c.id); setPage(0); }}
              className={`w-full text-left px-2 py-1.5 rounded-lg text-xs mb-0.5 cursor-pointer transition flex items-center gap-2 ${activeCat === c.id ? 'bg-white/[0.06] text-white' : 'text-white/70 hover:bg-white/[0.03]'}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: c.color || '#607d8b' }} />
              <span className="flex-1 truncate">{c.label}</span>
              <span className="text-white/35 text-[10px]">{c.codeCount}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Códigos */}
      <div>
        <div className="surface-card rounded-xl p-3 mb-3 flex flex-wrap items-center gap-2">
          <div className="flex-1 min-w-[200px] flex items-center gap-2 bg-[#0d141b] border border-white/[0.06] rounded-lg px-3">
            <Search size={15} className="text-white/40" />
            <input type="text" value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setApplied(search.trim()); } }}
              placeholder="Buscar TRC ou nome do evento..."
              className="flex-1 bg-transparent py-2 text-sm text-white placeholder:text-white/35 outline-none" />
            {search && <button onClick={() => { setSearch(''); setApplied(''); setPage(0); }} className="text-white/45 hover:text-white"><X size={14} /></button>}
          </div>
          <button onClick={() => { setOnlyUnnamed((v) => !v); setPage(0); }}
            className={`px-3 py-2 rounded-lg text-xs font-bold cursor-pointer transition border ${onlyUnnamed ? 'bg-amber-500/15 text-amber-400 border-amber-500/40' : 'bg-white/[0.03] text-white/60 border-white/[0.06] hover:text-white'}`}>
            Só sem nome
          </button>
          <button onClick={() => setEditCode({ trc: 0 })}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-purple-500/10 border border-purple-500/30 text-purple-400 hover:bg-purple-500/20 transition cursor-pointer">
            <Plus size={14} /> Cadastrar TRC
          </button>
        </div>

        <div className="surface-card rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-white/45 border-b border-white/[0.06]">
                  <th className="text-left py-2 px-3 font-bold">TRC</th>
                  <th className="text-left py-2 px-3 font-bold">Evento</th>
                  <th className="text-left py-2 px-3 font-bold">Categoria</th>
                  <th className="text-left py-2 px-3 font-bold">Notas</th>
                  <th className="text-right py-2 px-3 font-bold">Editar</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="py-10 text-center text-white/40"><Loader2 size={18} className="animate-spin inline" /></td></tr>
                ) : codes.length === 0 ? (
                  <tr><td colSpan={5} className="py-10 text-center text-white/40 text-sm">Nenhum TRC</td></tr>
                ) : codes.map((c) => {
                  const cat = c.categoryId != null ? catById[c.categoryId] : null;
                  return (
                    <tr key={c.trc} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                      <td className="py-2 px-3 font-mono text-white/85">{c.trc}</td>
                      <td className="py-2 px-3">
                        {c.eventName
                          ? <span className="text-white/90">{c.eventName}</span>
                          : <span className="text-white/30 italic">— sem nome —</span>}
                      </td>
                      <td className="py-2 px-3">
                        {cat ? (
                          <span className="inline-flex items-center gap-1.5 text-xs">
                            <span className="w-2 h-2 rounded-full" style={{ background: cat.color || '#607d8b' }} />
                            {cat.label}
                          </span>
                        ) : <span className="text-white/30 text-xs">—</span>}
                      </td>
                      <td className="py-2 px-3 text-white/55 text-xs truncate max-w-[240px]" title={c.notes || ''}>{c.notes || '—'}</td>
                      <td className="py-2 px-3 text-right">
                        <button onClick={() => setEditCode(c)}
                          className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/55 hover:text-[#00e1d7] cursor-pointer">
                          <Pencil size={13} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex items-center justify-between mt-3 text-xs text-white/55">
          <span>Página {page + 1} de {totalPages} · {total.toLocaleString('pt-BR')} códigos</span>
          <div className="flex gap-2">
            <button onClick={() => page > 0 && setPage((p) => p - 1)} disabled={page === 0}
              className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/75 hover:bg-white/[0.06] cursor-pointer disabled:opacity-40">Anterior</button>
            <button onClick={() => page + 1 < totalPages && setPage((p) => p + 1)} disabled={page + 1 >= totalPages}
              className="px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white/75 hover:bg-white/[0.06] cursor-pointer disabled:opacity-40">Próxima</button>
          </div>
        </div>
      </div>

      {newCatOpen && (
        <NewCategoryModal onClose={() => setNewCatOpen(false)}
          onCreated={() => { setNewCatOpen(false); loadCats(); }} />
      )}
      {editCode && (
        <EditCodeModal code={editCode} cats={cats}
          onClose={() => setEditCode(null)}
          onSaved={() => { setEditCode(null); loadCodes(); loadCats(); }} />
      )}
    </div>
  );
}

function NewCategoryModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { notify } = useToast();
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [color, setColor] = useState('#607d8b');
  const [busy, setBusy] = useState(false);
  const inCls = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';

  const save = async () => {
    if (name.trim().length < 2 || label.trim().length < 2) { notify('error', 'Preencha nome e label'); return; }
    setBusy(true);
    try {
      await createTrcCategory({ name: name.trim().toLowerCase(), label: label.trim(), color });
      notify('success', 'Categoria criada'); onCreated();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <TrcModal onClose={onClose} title="Nova categoria" icon={<Tags size={18} className="text-[#00e1d7]" />}>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Chave (sem espaço, ex: boss)</label>
          <input value={name} onChange={(e) => setName(e.target.value.replace(/\s/g, '').toLowerCase())} className={inCls} placeholder="boss" />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Label (exibição)</label>
          <input value={label} onChange={(e) => setLabel(e.target.value)} className={inCls} placeholder="Boss" />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Cor</label>
          <div className="flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="w-10 h-9 rounded bg-transparent border border-white/[0.06] cursor-pointer" />
            <input value={color} onChange={(e) => setColor(e.target.value)} className={`${inCls} font-mono`} />
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] cursor-pointer text-sm">Cancelar</button>
          <button onClick={save} disabled={busy} className="flex-1 px-4 py-2.5 rounded-lg bg-[#00e1d7] hover:bg-[#00c9c0] text-[#00252a] font-bold text-sm cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Criar
          </button>
        </div>
      </div>
    </TrcModal>
  );
}

function EditCodeModal({ code, cats, onClose, onSaved }: {
  code: TrcCode; cats: TrcCategory[]; onClose: () => void; onSaved: () => void;
}) {
  const { notify } = useToast();
  const isNew = !code.trc;
  const [trc, setTrc] = useState(code.trc ? String(code.trc) : '');
  const [catId, setCatId] = useState<number | ''>(code.categoryId ?? '');
  const [eventName, setEventName] = useState(code.eventName || '');
  const [notes, setNotes] = useState(code.notes || '');
  const [busy, setBusy] = useState(false);
  const inCls = 'w-full bg-[#0d141b] border border-white/[0.06] rounded-lg px-3 py-2 text-sm text-white';

  const save = async () => {
    const t = parseInt(trc, 10);
    if (!t || t < 1) { notify('error', 'TRC inválido'); return; }
    setBusy(true);
    try {
      await upsertTrcCode({
        trc: t, categoryId: catId === '' ? null : Number(catId),
        eventName: eventName.trim() || null, notes: notes.trim() || null,
      });
      notify('success', 'TRC salvo'); onSaved();
    } catch (err: any) { notify('error', err?.detail || err?.message || 'Erro'); }
    finally { setBusy(false); }
  };

  return (
    <TrcModal onClose={onClose} title={isNew ? 'Cadastrar TRC' : `Editar TRC ${code.trc}`}
      icon={<Code2 size={18} className="text-purple-400" />}>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Código TRC</label>
          <input value={trc} inputMode="numeric" disabled={!isNew}
            onChange={(e) => setTrc(e.target.value.replace(/\D/g, ''))}
            className={`${inCls} font-mono ${!isNew ? 'opacity-60' : ''}`} placeholder="12764" />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Nome do evento</label>
          <input value={eventName} onChange={(e) => setEventName(e.target.value)} className={inCls} placeholder="abrir special box" />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Categoria</label>
          <select value={catId} onChange={(e) => setCatId(e.target.value === '' ? '' : Number(e.target.value))}
            className={`${inCls} cursor-pointer`}>
            <option value="">— sem categoria —</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Notas (opcional)</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            className={`${inCls} resize-none`} placeholder="observações..." />
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] cursor-pointer text-sm">Cancelar</button>
          <button onClick={save} disabled={busy} className="flex-1 px-4 py-2.5 rounded-lg bg-purple-500 hover:bg-purple-400 text-white font-bold text-sm cursor-pointer disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
          </button>
        </div>
      </div>
    </TrcModal>
  );
}

function TrcModal({ children, onClose, title, icon }: {
  children: React.ReactNode; onClose: () => void; title: string; icon: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
           className="surface-card rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">{icon} {title}</h3>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer"><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

